<template>
  <v-card class="mb-4 px-6 py-4" width="50%">
    <div class="d-flex justify-space-between align-center mb-4">
      <div class="d-flex align-center">
        <p class="font-weight-bold mb-0 font-size-14">
          {{ $t('organization_users.licenses.title') }}
        </p>

        <v-btn
          class="ml-2 py-0"
          color="primary"
          rounded
          small
          text
          :to="`/organization-settings/${organizationId}?tab=admin`"
        >
          {{ $t('organization_users.licenses.manage_button') }}
        </v-btn>
      </div>

      <p class="caption mb-0 d-flex align-center">
        <b>{{ $t('organization_users.licenses.total_licenses') }}</b>
        &nbsp;
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          color="primary"
          :size="12"
          :width="2"
        />
        <template v-else>{{ formatNumber(totalLicenses) }}</template>
      </p>
    </div>

    <div class="d-flex justify-space-between align-center ">
      <p class="caption mb-0">
        <b>{{ $t('organization_users.licenses.active_licenses') }}</b>
        &nbsp;
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          color="primary"
          :size="12"
          :width="2"
        />
        <template v-else>{{ formatNumber(activeLicenses) }}</template>
      </p>

      <p class="caption mb-0">
        <b>{{ $t('organization_users.licenses.disabled_licenses') }}</b>
        &nbsp;
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          color="primary"
          :size="12"
          :width="2"
        />
        <template v-else>{{ formatNumber(disabledLicenses) }}</template>
      </p>

      <p class="caption mb-0 d-flex align-center">
        <v-menu bottom max-width="200" offset-y open-on-hover>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="mr-1" color="blue-grey darken-3" size="18">
              mdi-information
            </v-icon>
          </template>

          <div class="grey darken-4 px-3 py-5 text-center">
            <p class="text-body-2 white--text">
              {{ $t('organization_users.licenses.remaining_licenses_tooltip') }}
            </p>
          </div>
        </v-menu>
        <b>{{ $t('organization_users.licenses.remaining_licenses') }}</b>
        &nbsp;
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          color="primary"
          :size="12"
          :width="2"
        />
        <template v-else>{{ formatNumber(remainingLicenses) }}</template>
      </p>
    </div>
  </v-card>
</template>

<script>
import api from '@/api'

export default {
  name: 'UserLicensesCard',

  props: {
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoading: true,
      hasError: false,
      totalLicenses: 0,
      activeLicenses: 0,
      disabledLicenses: 0,
      remainingLicenses: 0,
    }
  },

  created() {
    this.fetchInfo()
  },

  methods: {
    async fetchInfo() {
      this.isLoading = true

      try {
        const [
          totalLicenses,
          totalActiveLicenses,
          totalDisabledLicenses,
        ] = await Promise.all([
          this.fetchTotalLicenses(),
          this.fetchTotalActiveLicenses(),
          this.fetchTotalDisabledLicenses(),
        ])

        this.totalLicenses = totalLicenses
        this.activeLicenses = totalActiveLicenses
        this.disabledLicenses = totalDisabledLicenses
        this.remainingLicenses = totalLicenses - totalActiveLicenses
      } catch (error) {
        this.hasError = true
      } finally {
        this.isLoading = false
      }
    },

    async fetchTotalLicenses() {
      const response = await api.organization.getById(this.organizationId)
      return response.data.licenses
    },

    async fetchTotalActiveLicenses() {
      const response = await api.account.getAccounts(this.organizationId, {
        limit: 1,
        filters: { isDeleted: false },
      })

      return response.data.totalRecordsInData
    },

    async fetchTotalDisabledLicenses() {
      const response = await api.account.getAccounts(this.organizationId, {
        limit: 1,
        filters: { isDeleted: true },
      })

      return response.data.totalRecordsInData
    },

    formatNumber(number) {
      return new Intl.NumberFormat('en-US').format(number)
    },
  },
}
</script>

<style></style>
